import { memo, useCallback, useMemo, useState } from "react";
import { useStore } from "../../store/globalState";
import { useSessionStore } from "../../store/useSessionStore";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { BodyCopy } from "../../components/BodyCopy";
import {
  AuraLayout,
  BankIcon,
  Button,
  CashSpecialIcon,
  Layout,
  WalletIcon,
  WarningIcon,
} from "lola-framework-ui-test";
import { M2M_ROUTES } from "../../constants";
import { FlagCircle } from "../../components/FlagCircle";
import { QuoteInfo } from "../../components/m2m/QuoteInfo";
import { amountFormatter } from "../../utils";
import { ExchangeFeeInfo } from "../../components/ExchangeFeeInfo";
import { PayoutInfo } from "../../components/PayoutInfo";
import { M2mFirstSendWarning } from "../../components/m2m/M2mFirstSendWarning";
import { Popup } from "../../components/Popup";
import { ScrollFadeIndicator } from "../../components/ScrollFadeIndicator";
import { SelectPaymentMethodSlot } from "../../components/SelectPaymentMethodSlot";
import { DestinationAccountInfo } from "../../components/DestinationAccountInfo";
import type { paymentType } from "../../interfaces/payment";
import masterCardLogo from "../../assets/master_card_icon.svg";
import visaLogo from "../../assets/visa_color_icon.svg";
import { useQuote } from "../../hooks/useQuote";
import { COUNTRY_CURRENCY } from "../../hooks/useExchangeRate";
import { MOCK_EXCHANGE_RATE } from "../../gallery/mockData";
import { formatBankAccountLabel } from "../../utils/bankAccountLabel";
import { Navbar } from "../../components/Navbar";

const mapPaymentTypeToMethod = (type: paymentType | undefined): string | null => {
  if (!type) return null;
  switch (type) {
    case "CARD":
      return "card";
    case "BANK":
      return "bank_account";
    case "CASH":
      return "cash";
    default:
      return null;
  }
};

/**
 * Gallery-safe m2m Transfer screen — flavor A. Faithful to the production
 * flavor-a layout (centered title in the Navbar, chip-based exchange info) but
 * driven entirely by the mock `useQuote` hook: no pricing, CIP or payment-method
 * APIs. On confirm it navigates to Sending Money flavor A.
 */
export const TransferFlavorAPage = memo(() => {
  const { t } = useTranslation("m2m");
  const { theme, amountState, paymentState, setQuoteState } = useStore();
  const { session } = useSessionStore();
  const { amount, anchor: amountAnchor = "send" } = amountState;
  const colorConfig = theme.whiteView;
  const { backgroundBtn, textColorBtn, title, bodyCopy } = colorConfig;
  const navigate = useNavigate();
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [showDestinationPopup, setShowDestinationPopup] = useState(false);
  const [showExcratePopup, setShowExcratePopup] = useState(false);
  const [shakePayment, setShakePayment] = useState(false);

  const sourceCountry = session.data?.config?.sourceCountry || "US";
  const destinationCountry = session.data?.config?.destinationCountry || "MX";
  const sourceCurrency = COUNTRY_CURRENCY[sourceCountry] || "USD";
  const destinationCurrency = COUNTRY_CURRENCY[destinationCountry] || "MXN";

  const fundingMethod = mapPaymentTypeToMethod(paymentState.payment?.type) || "card";
  const cardId =
    paymentState.payment?.type === "CARD"
      ? paymentState.payment?.cardId
      : undefined;

  const numericAmount = amount ? parseFloat(amount) : NaN;
  const hasAmount = !Number.isNaN(numericAmount) && numericAmount > 0;
  const quoteSendAmount = hasAmount
    ? amountAnchor === "receive"
      ? Number((numericAmount / MOCK_EXCHANGE_RATE).toFixed(2))
      : numericAmount
    : null;

  const { quote, loading: quoteLoading } = useQuote(
    sourceCountry,
    destinationCountry,
    fundingMethod,
    "bank_account",
    quoteSendAmount,
    sourceCurrency,
    cardId
  );

  const blockForPricing =
    Boolean(paymentState.payment) && (quoteLoading || !quote);

  const handleConfirmClick = useCallback(() => {
    if (!paymentState.payment) {
      setShakePayment(true);
      setTimeout(() => setShakePayment(false), 500);
      return;
    }
    if (blockForPricing || !quote) {
      return;
    }
    setQuoteState(quote);
    navigate(M2M_ROUTES.SENDING);
  }, [paymentState.payment, blockForPricing, quote, setQuoteState, navigate]);

  const exchangeRate = quote?.exchangeRate ?? 0;
  const fee = quote?.fee ?? 0;

  const sendFormatted = useMemo(() => {
    if (quoteLoading) return "...";
    if (quote?.sendAmount == null) return undefined;
    return amountFormatter(quote.sendAmount, sourceCurrency, {
      country: "en-US",
    });
  }, [quote, quoteLoading, sourceCurrency]);

  const receiveFormatted = useMemo(() => {
    if (quoteLoading) return "...";
    if (quote?.receiveAmount == null) return undefined;
    return amountFormatter(quote.receiveAmount, destinationCurrency, {
      country: "en-US",
    });
  }, [quote, quoteLoading, destinationCurrency]);

  const totalPayment = useMemo(() => {
    if (quote?.totalCost) {
      return amountFormatter(quote.totalCost);
    }
    if (amount) {
      return amountFormatter(parseFloat(amount) + fee);
    }
    return "0";
  }, [quote, amount, fee]);

  const feeDisplay = useMemo(() => {
    return `$${fee.toFixed(2)}`;
  }, [fee]);

  const pickIcon = useCallback((type: paymentType, brand: string) => {
    switch (type) {
      case "CARD": {
        let finalIcon = null;
        if (brand.toLocaleUpperCase().includes("VISA")) finalIcon = visaLogo;
        if (brand.toLocaleUpperCase().includes("MASTERCARD"))
          finalIcon = masterCardLogo;

        return {
          brand,
          icon: <img src={finalIcon ?? ""} alt={`${brand} logo`} />,
        };
      }
      case "BANK":
        return {
          brand,
          icon: <BankIcon size={20} />,
        };
      case "CASH":
        return {
          brand,
          icon: <CashSpecialIcon size={20} />,
        };

      default:
        return {
          brand,
          icon: <WalletIcon size={20} />,
        };
    }
  }, []);

  const paymentSelected = useMemo(
    () =>
      paymentState.payment
        ? pickIcon(paymentState.payment.type, paymentState.payment.brand)
        : {
            brand: t("transfer.selectPaymentMethod"),
            icon: <WalletIcon size={20} />,
          },
    [paymentState, pickIcon, t]
  );

  return (
    <>
      <AuraLayout
        colorConfig={colorConfig}
        className="scrollable white-view-background flavor-a"
      >
        <Layout.Header>
          <Navbar
            title={t("transfer.title")}
            color={title}
            align="center"
            onBackClick={() => navigate(-1)}
          />
        </Layout.Header>
        <Layout.Content className="transfer-section">
          <ScrollFadeIndicator color={bodyCopy} />
          <section>
            <section className="estimated-quote-section">
              <section className="quote-container">
                <QuoteInfo
                  label={t("transfer.from")}
                  currency={sourceCurrency}
                  textColor={bodyCopy}
                  mode="input"
                  side="send"
                  isActive={amountAnchor === "send"}
                  derivedAmount={
                    amountAnchor === "receive"
                      ? sendFormatted || undefined
                      : undefined
                  }
                  icon={<FlagCircle iso2={sourceCountry.toLowerCase()} />}
                />
                <QuoteInfo
                  label={t("transfer.to")}
                  currency={destinationCurrency}
                  textColor={bodyCopy}
                  mode="input"
                  side="receive"
                  isActive={amountAnchor === "receive"}
                  derivedAmount={
                    amountAnchor === "send"
                      ? receiveFormatted || undefined
                      : undefined
                  }
                  hideCurrencySymbol
                  icon={<FlagCircle iso2={destinationCountry.toLowerCase()} />}
                />
                <ExchangeFeeInfo
                  fee={quoteLoading ? "..." : exchangeRate.toFixed(4)}
                  currency={destinationCurrency}
                  variant="transfer"
                  onClick={() => setShowExcratePopup(true)}
                />
              </section>
            </section>
            <section className="payout-container">
              <PayoutInfo
                title={t("transfer.payingWith")}
                subtitle={paymentSelected.brand}
                icon={paymentSelected.icon}
                background={backgroundBtn}
                color={textColorBtn}
                onChange={() => setShowPaymentPopup(true)}
                className={`${!paymentState.payment ? "payment-required" : ""} ${
                  shakePayment ? "shake" : ""
                }`.trim()}
              />
              <PayoutInfo
                title={t("transfer.receiveIn")}
                subtitle={
                  formatBankAccountLabel(
                    session.data?.userData?.paymentMethod?.bankAccount?.bankName,
                    session.data?.userData?.paymentMethod?.bankAccount
                      ?.accountNumber,
                  ) ?? t("transfer.bankAccount", { last4: "1234" })
                }
                icon={<BankIcon size={20} />}
                background={backgroundBtn}
                color={textColorBtn}
                onChange={() => setShowDestinationPopup(true)}
              />
              <M2mFirstSendWarning
                sendAmount={quote?.sendAmount ?? quoteSendAmount}
                sourceCurrency={sourceCurrency}
              />
              <p className="footer-text foreground transfer-timing-note">
                <b>{t("transfer.instantTransfer")}</b>
                {" "}
                <span className="transfer-timing-note-rest">
                  {t("transfer.mayTakeFew")}
                </span>
              </p>
            </section>
          </section>
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section">
            <section className="footer-summary">
              <p className="footer-text foreground footer-summary-title">
                <b>{t("transfer.detail")}</b>
              </p>
              <p className="footer-text foreground">
                {t("transfer.fee", { amount: feeDisplay, currency: sourceCurrency })}
              </p>
              <p className="footer-text foreground">
                {t("transfer.totalPayment", {
                  amount: `$${totalPayment}`,
                  currency: sourceCurrency,
                })}
              </p>
            </section>
            <div
              className={`button-wrapper ${
                amount && paymentState.payment && quote && !quoteLoading
                  ? "rainbow-ready"
                  : ""
              }`}
            >
              <Button
                size="small"
                background={backgroundBtn}
                color={textColorBtn}
                disabled={blockForPricing}
                onClick={handleConfirmClick}
              >
                {quoteLoading
                  ? t("transfer.loadingQuote", "Loading...")
                  : t("transfer.confirmTransfer")}
              </Button>
            </div>
          </section>
        </Layout.Footer>
      </AuraLayout>
      <Popup visible={showPaymentPopup} onClose={() => setShowPaymentPopup(false)}>
        <SelectPaymentMethodSlot onClose={() => setShowPaymentPopup(false)} />
      </Popup>
      <Popup
        visible={showDestinationPopup}
        onClose={() => setShowDestinationPopup(false)}
      >
        <DestinationAccountInfo onClose={() => setShowDestinationPopup(false)} />
      </Popup>

      <Popup
        visible={showExcratePopup}
        onClose={() => setShowExcratePopup(false)}
      >
        <section className="qoute-popup">
          <WarningIcon size={60} />
          <BodyCopy textColor={bodyCopy}>
            <Trans i18nKey="transfer.quote.disclaimer" ns="m2m" />
          </BodyCopy>
        </section>
      </Popup>
    </>
  );
});
